import type { MinionsPayload } from '../report';
import { useMinionsI18n } from '../i18n';
import { avgSessionSec, currentWeek, funnelLosses, funnelSteps, playsPerPlayer, share, timePerPlayerSec } from '../derive';
import { DailyChart } from './Charts';
import { Kpi, Tile } from './Parts';

/*
 * The top of the page in the design's order: the take-away line, reach with
 * the opt-in funnel, the headline tiles and the daily chart with its table.
 * Rates are derived here from the payload's counts; a figure the source cannot
 * give reads "—".
 */

type P = { data: MinionsPayload };

export function TakeAway({ data }: P) {
  const { t, f } = useMinionsI18n();
  const c = data.totals;
  return (
    <p className="mn-take">
      <b>{f.int(c.uniquePlayers)}</b> {t.tk1} <b>{f.dec(playsPerPlayer(c))}</b> {t.tk3}
    </p>
  );
}

/** Funnel row fills, top to bottom, as the design shades them. */
const SHADES = ['ink', 'blue2', undefined] as const;

export function ReachCard({ data }: P) {
  const { t, f } = useMinionsI18n();
  const c = data.totals;
  const steps = funnelSteps(c);
  const { losses, worst } = funnelLosses(steps);
  const top = c.uniqueVisitors;
  const week = currentWeek(data.weeks);
  return (
    <section className="mn-card" id="reach">
      <div className="mn-head">
        <h2 className="mn-eyebrow">
          {t.uniquePlayers}
          <Kpi n="1" />
        </h2>
        {week && <span className="mn-tag">{t.weekTag(week.week, data.campaign.totalWeeks)}</span>}
      </div>
      <div className="mn-hero">
        <span className="mn-big">{f.int(c.uniquePlayers)}</span>
        <span className="mn-note">{t.funnelSub}</span>
      </div>
      <div className="mn-funnel">
        {steps.map((s, i) => {
          const loss = losses[i];
          const tone = SHADES[i];
          return (
            <div key={s.key} className={`mn-frow${i === worst ? ' is-max' : ''}`}>
              <div className="mn-frow-top">
                <span>{t.funnel[s.key]}</span>
                <span>{`${f.int(s.users)} · ${f.pct(share(s.users, top))}`}</span>
              </div>
              <div className="mn-track">
                {top > 0 && <div className={`mn-fill${tone ? ` ${tone}` : ''}`} style={{ width: `${Math.min(100, (s.users / top) * 100)}%` }} />}
              </div>
              {loss != null && <span className={`mn-loss${i === worst ? ' is-max' : ''}`}>{t.lost(f.int(loss))}</span>}
            </div>
          );
        })}
      </div>
      <p className="mn-note">{t.funnelFoot}</p>
    </section>
  );
}

export function HeadlineTiles({ data }: P) {
  const { t, f } = useMinionsI18n();
  const c = data.totals;
  return (
    <div className="mn-tiles" id="tiles">
      <Tile label={t.kPlays} kpi="3" value={f.int(c.plays)} sub={t.kPlaysSub} />
      <Tile label={t.kPpu} kpi="4" value={f.dec(playsPerPlayer(c))} sub={t.kPpuSub(data.campaign.dailyGames)} />
      <Tile label={t.kTickets} kpi="5" value={f.int(c.tickets)} sub={t.kTicketsSub} />
      <Tile label={t.kTime} value={f.hours(c.totalTimeSec)} sub={t.kTimeSub} />
      <Tile
        label={t.kSession}
        kpi="7"
        value={f.dur(avgSessionSec(c))}
        sub={
          <>
            {t.kSessionSub} <b>{f.dur(timePerPlayerSec(c))}</b> {t.kSessionSub2}
          </>
        }
      />
    </div>
  );
}

export function DailyCard({ data }: P) {
  const { t, f } = useMinionsI18n();
  const measured = data.days.filter((d) => d.visitors != null);
  const today = measured.at(-1)?.date;
  return (
    <section className="mn-card" id="daily">
      <div className="mn-head">
        <h2 className="mn-h">{t.dailyTitle}</h2>
        <div className="mn-legend">
          <span>
            <i className="mn-sw soft" /> {t.lgVisitors}
          </span>
          <span>
            <i className="mn-sw ln" /> {t.lgDau}
          </span>
        </div>
      </div>
      <DailyChart days={data.days} />
      <p className="mn-note">{t.dailyNote}</p>
      {measured.length > 0 && (
        <details className="mn-details">
          <summary>{t.dailyShow}</summary>
          <div className="mn-tablebox">
            <table aria-label={t.dailyTitle}>
              <thead>
                <tr>
                  <th scope="col">{t.cDay}</th>
                  <th scope="col">{t.cVisitors}</th>
                  <th scope="col">{t.cDau}</th>
                  <th scope="col">{t.cPlays}</th>
                </tr>
              </thead>
              <tbody>
                {[...measured].reverse().map((d) => (
                  <tr key={d.date} className={d.date === today ? 'cur' : undefined}>
                    <td>{f.day(d.date, 'short')}</td>
                    <td>{f.int(d.visitors)}</td>
                    <td>{f.int(d.activePlayers)}</td>
                    <td>{f.int(d.plays)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  );
}
